import React, { useState, useEffect, useMemo } from 'react';
import { Segment, Switch, Radio } from 'tea-component';
import CardSlotV2 from '@/pages/intelligence/components/CardSlotV2';
import DynamicColTable from './DynamicColTable';
import { getLocale, useSelector } from 'umi';
import CardStats from '@/pages/intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Stats/components/CardStats';
import Visualis from './Visualis';
import useIntlExtend from '@/pages/intelligence/hooks/useIntlExtend';
import { pageCfgType } from '@intelligence/pages/TopCharts/PCGames/components/GamesDetails/Stats/_config';
import {
  getHasDeviceBtn,
  getChartDisableType,
} from '@intelligence/pages/TopCharts/PCGames/components/GamesDetails/Stats/_utils';
import classNames from 'classnames';
import {
  ChartType,
  PcConsoleAllDetailsSegment,
} from '@/pages/intelligence/pages/TopCharts/_config/enums';
import { PCConsoleMetricSegementList } from '@/pages/intelligence/pages/TopCharts/_config/metricOptions';
import { isObjectEmpty } from '@/pages/intelligence/utils/commonUtils';
import { ChartContext } from '@/pages/intelligence/layouts';
import { getTrendsChartGridRightByGranularity } from '@intelligence/v3/utils/echarts';
import { DBI_TOPCHARTS } from '@intelligence/models/topcharts';
import { getFirstWeekSinceReleaseDates, isFirstWeekSinceReleaseShortcut } from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/utils';

import type { IMetricExplanationTipsClickHandler } from '@intelligence/v3/components/MetricExplanationTips';

interface StatsContextType {
  cardTitle?: React.ReactNode;
  subTitle?: React.ReactNode;
  defaultByDeviceVal?: boolean;
  noDataMessage?: React.ReactNode;
  pageCfg: pageCfgType;
  pageId: string;
  entityType: string;
  cardStatus: {
    isLoading: boolean;
    isError: boolean;
    isNoData: boolean;
  };
  pageSortAttr?: {
    currentSortValue: string;
    currentSortMetric: string;
    currentRatioType: number;
  };
  tableData: any[];
  fixedTableData?: any[]; // 固定在首行的内容
  barData?: any;
  pieData?: any;
  lineData?: any;
  stackBarData?: any;
  stackLineData?: any;
  stackPieData?: any;
  chartType: string;
  setChartType: Function;
  metricSegVal: string;
  setMetricSegVal: Function;
  earliestDay?: string;
  baseReleaseTime?: string;
  firstWeekReleaseTime?: string;
  earliestPeriod?: string;
  dateType: string;
  fetchParams?: any;
  chartLoading: boolean;
  setChartLoading: Function;
  visualisMetric?: any;
  visualisTitleNow?: any;
  isStepLine?: boolean;
  marketLineLoading?: boolean;
  ifUseMarketLineLoading?: boolean;
  isCountriesPage?: boolean;
  gameName?: string;
  gameCover?: string;
  startDate?: string;
  endDate?: string;
  calOtherList?: any;
  changeDeviceBtn?: Function;
  isDropdown?: boolean;
  regionsCountries: any;
  sumAvgMaxList: any[];
  visuailsMetricValues?: any[];
  topListTmpCur?: any;
  showRadio?: boolean;
  radioValue?: string;
  setRadioValue?: Function;
  getTop5DataList?: Function;
  isPercentage?: boolean;
  isPercentageDecimal?: boolean;
  ifLineChartLegendOn?: boolean;
  rightDom?: React.ReactNode;
  leftDom?: React.ReactNode;
  chartNodataMsg?: string;
  chartIsNoData?: boolean;
  legendSelected?: any;
  className?: string;
  mainSegment?: string;
  trendTitle?: string;
  lineSourceName?: string;
  sourceList?: string[];
  yAxisConfig?: Record<string, any>;
  /**
   * Countries UserEngagement 堆叠折线图 tooltip 展示 all 合计值
   */
  stackLineTooltipShowAll?: boolean;
  /**
   * 展示的柱图数量
   */
  showChartsItemNum?: number;
  /**
   * tooltip 额外配置
   */
  tooltip?: Record<string, any>;
  onMetricExplanationClick?: IMetricExplanationTipsClickHandler;
}

const StatsContext = (props: StatsContextType) => {
  const {
    cardTitle: cardTitleProp,
    subTitle,
    pageId,
    cardStatus,
    entityType,
    defaultByDeviceVal,
    noDataMessage,
    pageCfg,
    pageSortAttr,
    visuailsMetricValues = [],
    tableData = [],
    fixedTableData,
    barData = [],
    pieData = [],
    lineData = [],
    chartType: chartTypeProp,
    setChartType: setChartTypeProp,
    metricSegVal,
    setMetricSegVal,
    earliestDay,
    dateType,
    chartLoading,
    setChartLoading,
    baseReleaseTime,
    firstWeekReleaseTime,
    earliestPeriod,
    visualisMetric,
    visualisTitleNow,
    isStepLine,
    isCountriesPage,
    gameName,
    gameCover,
    calOtherList = [],
    changeDeviceBtn,
    isDropdown,
    regionsCountries,
    stackBarData,
    stackLineData,
    stackPieData,
    startDate,
    endDate,
    sumAvgMaxList,
    showRadio,
    radioValue,
    setRadioValue,
    isPercentage,
    isPercentageDecimal,
    rightDom,
    leftDom,
    chartNodataMsg,
    chartIsNoData,
    legendSelected,
    className,
    marketLineLoading,
    ifUseMarketLineLoading = false,
    mainSegment,
    trendTitle,
    getTop5DataList,
    topListTmpCur,
    lineSourceName,
    ifLineChartLegendOn = false,
    sourceList,
    yAxisConfig,
    stackLineTooltipShowAll,
    showChartsItemNum,
    tooltip,
    onMetricExplanationClick,
  } = props;

  const topchartsState = useSelector((state: any) => state?.[DBI_TOPCHARTS] || {});
  const lineXAxisData = useMemo<string[] | undefined>(() => {
    if (!isFirstWeekSinceReleaseShortcut(topchartsState?.selectedTimeShortcutType)) {
      return undefined;
    }
    const firstWeekSinceReleaseDates = getFirstWeekSinceReleaseDates({
      dateType,
      startDate,
      endDate,
      releaseDate: firstWeekReleaseTime,
    });
    if (!firstWeekSinceReleaseDates.length) {
      return undefined;
    }
    return firstWeekSinceReleaseDates;
  }, [topchartsState?.selectedTimeShortcutType, dateType, startDate, endDate, firstWeekReleaseTime]);

  // console.log('StatsContextStatsContext', props);

  const ChartConsumer = ChartContext.Consumer; // 订阅消费者
  const isAllMetricsPage = pageId === 'allMetrics—allMetrics';
  const gridConfig = {
    height: isAllMetricsPage ? '280px' : '250px',
    top: isAllMetricsPage ? 30 : undefined,
    right: getTrendsChartGridRightByGranularity(isAllMetricsPage ? 50 : 40, dateType),
  };

  const {
    cardTitle,
    disableChartType,
    defaultChartType,
    segementList,
    tableMetricList,
    chartIconList,
    sumavgArr: sumavgArrPage, // 页面配置里有则用配置里的，否则用指标配置里的
  } = pageCfg || {};
  const metricItem = tableMetricList?.find((t) => {
    return t?.value === metricSegVal;
  });
  const locale = getLocale(); // 中英切换方法
  const { formatMessage } = useIntlExtend();
  const { isLoading, isError, isNoData } = cardStatus || {};
  const [byDevice, setByDevice] = useState(defaultByDeviceVal);
  const [chartType, setChartType] = useState(chartTypeProp);
  // 外部切换 chartType 状态，内部状态同步更新
  useEffect(() => {
    setChartType(chartTypeProp);
  }, [chartTypeProp]);

  /**
   * 当前指标是否显示按设备拆分图表按钮
   * 国家页堆叠柱图 usser engagement ampere 数据源使用 hasByDeviceBar 属性
   * 其余页面指标使用 hasByDevice
   */
  const metricHasDeviceBtn =
    isCountriesPage && metricItem?.dataSource === 'ampere'
      ? metricItem?.hasByDeviceBar
      : metricItem?.hasByDevice;

  const showDeviceBtn = getHasDeviceBtn({
    chartType,
    // metricHasDeviceBtn: metricItem?.hasByDevice,
    metricHasDeviceBtn,
    isCountriesPage,
    entityType,
  });

  const newdisableChartType = getChartDisableType({
    hasDeviceBtn: showDeviceBtn,
    disableChartType,
    isCountriesPage,
  });

  useEffect(() => {
    // 用图标列表过滤掉被禁用的，如果有默认icon则用，否则用非禁用ICON的第一个值
    const activeIconList = chartIconList?.filter((item) => {
      return !newdisableChartType.includes(item?.value);
    });
    const defaultChartItem = activeIconList?.find((t) => {
      return t?.value === defaultChartType;
    });
    // 不要调外面的函数 ，外面的函数可能在判断调用接口
    setChartType(defaultChartItem?.value || activeIconList?.[0]?.value);
  }, [JSON.stringify(newdisableChartType)]);

  const getSumAvgList = (sumavgArr: string[], sumAvgMaxList: any[]) => {
    if (Array.isArray(sumavgArr) && Array.isArray(sumAvgMaxList)) {
      return sumavgArr
        ?.map((t: string) => {
          return sumAvgMaxList?.find((listItem) => {
            return listItem.date === t;
          });
        })
        .filter((item) => item !== undefined);
    }
    return [];
  };
  // 页面配置里有则用配置里的，否则用指标配置里的
  const sumAvgList = getSumAvgList(
    !!sumavgArrPage?.length ? sumavgArrPage : metricItem?.sumavgArr,
    sumAvgMaxList,
  );

  const changeMainSegment = (value: string) => {
    setMetricSegVal?.(value);
    getTop5DataList && getTop5DataList(topListTmpCur.list || [], value);
  };

  const changeDevice = (value: boolean) => {
    // console.log(value);
    // TODO 处理可视化数据
    setByDevice(value);
    changeDeviceBtn?.(value);
  };
  const deviceRealVal = byDevice && showDeviceBtn;
  const showStatusCard = isLoading || isNoData || isError || isDropdown;

  const isPCPcu = useMemo(() => {
    return entityType == 'pc' && mainSegment == 'pcu';
  }, [mainSegment, entityType]);

  const countriesCardTitleKey = useMemo(() => {
    if (
      mainSegment == 'steamSales' ||
      (entityType == 'combine' && mainSegment == 'pcu') ||
      isPCPcu
    ) {
      return 'dbi_detail_global_data';
    }
    return 'dbi_detail_countries_share';
  }, [mainSegment, entityType]);

  const countriesShareTitle = useMemo(() => {
    const metricList = [...PcConsoleAllDetailsSegment, ...PCConsoleMetricSegementList];
    const metricObj: any = metricList?.find((item: any) => item.value === visualisMetric) || {};
    // dbi_detail_global_data
    if (isObjectEmpty(metricObj)) {
      formatMessage({ id: countriesCardTitleKey });
    } else {
      return locale === 'en-US'
        ? `${formatMessage({ id: countriesCardTitleKey })} of ${formatMessage({
            id: isPCPcu ? 'dbi_detail_pcu_and_acu' : metricObj.text,
          })}`
        : `${formatMessage({
            id: isPCPcu ? 'dbi_detail_pcu_and_acu' : metricObj.text,
          })}${formatMessage({ id: countriesCardTitleKey })}`;
    }
  }, [locale, visualisMetric, PCConsoleMetricSegementList, mainSegment, entityType]);

  // 根据不同场景返回secondTitleRight
  const bubbleObjCur = useMemo(() => {
    switch (pageId) {
      case 'digitalRevenue':
      case 'physicalRevenue':
      case 'gsd': {
        const typeList = ['pc', 'combine', 'console'];
        const lineSourceObj = {
          digitalRevenue: 'mscience',
          physicalRevenue: 'npd',
          gsd: 'gsd',
        };
        const isNewObj = typeList.includes(entityType);
        const secondRigText = isNewObj ? visualisTitleNow : formatMessage({ id: trendTitle });
        const lineSource =
          isNewObj && !isCountriesPage && !deviceRealVal ? lineSourceObj[pageId] : '';
        const lineSourceNameText =
          isNewObj && !deviceRealVal && !isCountriesPage ? ' ' : lineSourceName;
        return {
          secondTitleRight: secondRigText,
          lineSourceIcon: lineSource,
          lineSourceName: lineSourceNameText,
        };
      }
      case 'pcu': {
        const typeList = ['pc'];
        const isNewObj = typeList.includes(entityType);
        const secondRigText = isNewObj ? ' ' : formatMessage({ id: trendTitle });
        const lineSource = '';
        const lineSourceNameText = lineSourceName;
        return {
          secondTitleRight: secondRigText,
          lineSourceIcon: lineSource,
          lineSourceName: lineSourceNameText,
        };
      }
      default:
        return {
          secondTitleRight: formatMessage({ id: trendTitle }),
          lineSourceIcon: '',
          lineSourceName,
        };
    }
  }, [locale, metricSegVal, pageId, mainSegment, entityType, deviceRealVal]);

  return (
    <div className="dbi-trends-card">
      <CardSlotV2
        className={classNames(
          'dbi-mobileGames-card-context',
          'dbi-mobileGames-title-custom',
          className,
        )}
        title={
          cardTitleProp
            ? cardTitleProp
            : isCountriesPage
            ? countriesShareTitle
            : formatMessage({ id: cardTitle })
        }
        subTitle={subTitle}
        activeTab={chartType}
        // chartIconList是在pageCfg的配置里配的，不需要额外判断
        iconList={showStatusCard ? [] : chartIconList}
        rightDom={rightDom}
        rightText={
          !showStatusCard && earliestDay
            ? `${formatMessage({
                id: `dbi_detail_earliest_date`,
              })}: ${earliestDay}`
            : ''
        }
        rightTip={
          !showStatusCard && earliestDay
            ? formatMessage({
                id:
                  mainSegment === 'newzooMetrics'
                    ? 'dbi_detail_earlistdate_info_pcconsole'
                    : 'dbi_detail_earlistdate_info',
              })
            : ''
        }
        disableChartType={newdisableChartType}
        getChange={(value: any) => {
          setChartType?.(value);
          setChartTypeProp?.(value);
        }}
      >
        {Array.isArray(segementList) && segementList.length > 1 && (
          <Segment
            value={metricSegVal}
            onChange={(value) => {
              changeMainSegment(value);
            }}
            options={segementList?.map((t) => {
              return { ...(t || {}), text: formatMessage({ id: t?.text }) };
            })}
          />
        )}

        {showRadio && (
          <Radio.Group
            style={{ marginRight: 20 }}
            value={radioValue}
            onChange={(value) => setRadioValue?.(value)}
          >
            <Radio name="Full Game">Base Game</Radio>
            <Radio name="All">All</Radio>
          </Radio.Group>
        )}

        {showDeviceBtn && (
          <ChartConsumer>
            {(themeContextValue) => {
              const { ifResetLegend, setIfResetLegend } = themeContextValue;
              return (
                <Switch
                  value={byDevice}
                  onChange={(value) => {
                    setIfResetLegend(!ifResetLegend);
                    changeDevice?.(value);
                  }}
                >
                  {/* {formatMessage({ id: `dbi_by_device` })} */}
                  {formatMessage({ id: `dbi_by_platform` })}
                </Switch>
              );
            }}
          </ChartConsumer>
        )}

        {showStatusCard ? (
          <CardStats
            cardStatus={cardStatus}
            noDataMessage={noDataMessage}
            isDropdown={isDropdown}
          />
        ) : (
          <>
            {leftDom}
            <div className="trends-card-line">
              <Visualis
                chartNodataMsg={chartNodataMsg}
                tableData={tableData}
                byDevice={deviceRealVal}
                chartType={chartType}
                dateType={dateType}
                startDate={startDate}
                endDate={endDate}
                imageType={entityType}
                barData={deviceRealVal ? stackBarData : barData}
                lineData={deviceRealVal ? stackLineData : lineData}
                pieData={deviceRealVal ? stackPieData : pieData}
                isNodata={chartIsNoData ?? isNoData}
                metric={visualisMetric}
                chartLoading={chartLoading}
                setChartLoading={setChartLoading}
                marketLineLoading={marketLineLoading}
                ifUseMarketLineLoading={ifUseMarketLineLoading}
                topChartsType={pageId}
                titleNow={visualisTitleNow}
                baseReleaseTime={baseReleaseTime}
                earliestPeriod={earliestPeriod}
                barNameListFormatter={pageCfg?.barNameListFormatter}
                lineSeriesFormatter={
                  deviceRealVal ? pageCfg?.stackLineSeriesFormatter : pageCfg?.lineSeriesFormatter
                }
                // 堆叠柱图的平台列表，避免新接口 compare 从数据中取时无数据
                sourceList={sourceList}
                barSeriesFormatter={
                  deviceRealVal ? pageCfg?.stackBarSeriesFormatter : pageCfg?.barSeriesFormatter
                }
                pieSeriesFormatter={
                  deviceRealVal ? pageCfg?.stackPieSeriesFormatter : pageCfg?.pieSeriesFormatter
                }
                lineMetricName={pageCfg?.lineMetricName}
                visualisLineTopChartsType={pageCfg?.visualisLineTopChartsType}
                visualisBarTopChartsType={pageCfg?.visualisBarTopChartsType}
                enableAutoBarWidth={pageCfg?.enableAutoBarWidth}
                visualisPieTopChartsType={pageCfg?.visualisPieTopChartsType}
                isStepLine={isStepLine}
                isCountriesPage={isCountriesPage}
                gameName={gameName}
                calOtherList={calOtherList}
                isStackLine={chartType === ChartType.StackedLine}
                regionsCountries={regionsCountries || []}
                isPercentage={isPercentage}
                yAxisConfig={yAxisConfig}
                isPercentageDecimal={isPercentageDecimal}
                visuailsMetricValues={visuailsMetricValues}
                tableMetricList={tableMetricList}
                legendSelected={legendSelected}
                ifLineChartLegendOn={ifLineChartLegendOn}
                secondTitleRight={bubbleObjCur.secondTitleRight}
                lineSourceIcon={bubbleObjCur.lineSourceIcon}
                lineSourceName={bubbleObjCur.lineSourceName}
                stackLineTooltipShowAll={stackLineTooltipShowAll}
                showChartsItemNum={showChartsItemNum}
                tooltip={tooltip}
                gridConfig={gridConfig}
                lineXAxisData={lineXAxisData}
              />
            </div>
            <DynamicColTable
              // tableData={[...(tableData || []), ...(sumAvgList || [])]}
              entityType={entityType}
              gameName={gameName}
              gameCover={gameCover}
              tableData={tableData || []}
              sumAvgList={sumAvgList}
              fixedTableData={fixedTableData}
              dateType={dateType}
              startDate={startDate}
              endDate={endDate}
              pageSortAttr={pageSortAttr}
              columnList={tableMetricList}
              firstCol={pageCfg?.firstCol}
              specialCol={pageCfg?.specialCol}
              getMetricValueOfCvm={pageCfg?.getMetricValueOfCvm}
              growthHasPoint={pageCfg?.growthHasPoint}
              pageSizeOptions={pageCfg?.pageSizeOptions}
              defaultPageSize={
                pageCfg?.defaultPageSize
                  ? pageCfg?.defaultPageSize
                  : pageCfg?.getDefaultPageSize?.(dateType)
              }
              tableSortMetricProperty={pageCfg?.tableSortMetricProperty}
              tableSortGrowthProperty={pageCfg?.tableSortGrowthProperty}
              regionsCountries={regionsCountries || []}
              onMetricExplanationClick={onMetricExplanationClick}
            />
          </>
        )}
      </CardSlotV2>
    </div>
  );
};
export default StatsContext;
